import { Star, MapPin, Award, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

export const AgentCard = ({ agent, variant = 'standard' }) => {
    const navigate = useNavigate();
    const isElite = agent.is_top_performer;

    const renderStars = (rating) => {
        const stars = [];
        const fullStars = Math.floor(rating);
        const hasHalf = rating % 1 >= 0.5;

        for (let i = 0; i < 5; i++) {
            if (i < fullStars) {
                stars.push(
                    <Star key={i} className="w-4 h-4 fill-scalimmo-gold text-scalimmo-gold" />
                );
            } else if (i === fullStars && hasHalf) {
                stars.push(
                    <Star key={i} className="w-4 h-4 fill-scalimmo-gold/50 text-scalimmo-gold" />
                );
            } else {
                stars.push(
                    <Star key={i} className="w-4 h-4 text-gray-300" />
                );
            }
        }
        return stars;
    };

    const defaultPhoto = `https://ui-avatars.com/api/?name=${agent.first_name}+${agent.last_name}&background=0F172A&color=fff&size=200`;

    return (
        <div
            className={`agent-card rounded-xl overflow-hidden bg-white border ${
                isElite ? 'elite-card' : 'border-border/60 shadow-soft'
            }`}
            data-testid={`agent-card-${agent.id}`}
        >
            {/* Image Section */}
            <div className="relative h-48 overflow-hidden">
                <img
                    src={agent.photo_url || defaultPhoto}
                    alt={`${agent.first_name} ${agent.last_name}`}
                    className="w-full h-full object-cover"
                />
                {isElite && (
                    <div className="absolute top-3 right-3">
                        <span className="elite-badge badge-elite">
                            <Award className="w-3.5 h-3.5" />
                            Top Performer
                        </span>
                    </div>
                )}
            </div>

            {/* Content */}
            <div className="p-5 space-y-4">
                {/* Name & Agency */}
                <div>
                    <h3 className="font-heading text-xl font-semibold text-scalimmo-navy">
                        {agent.first_name} {agent.last_name}
                    </h3>
                    {agent.agency_name && (
                        <p className="text-sm text-muted-foreground mt-0.5">
                            {agent.agency_name}
                        </p>
                    )}
                </div>

                {/* Location */}
                {agent.city && (
                    <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                        <MapPin className="w-4 h-4" />
                        {agent.city}
                    </div>
                )}

                {/* Rating */}
                <div className="flex items-center gap-2">
                    <div className="flex items-center gap-0.5">
                        {renderStars(agent.avg_rating || 0)}
                    </div>
                    <span className="text-sm font-medium text-scalimmo-navy">
                        {(agent.avg_rating || 0).toFixed(1)}
                    </span>
                    <span className="text-sm text-muted-foreground">
                        ({agent.total_reviews || 0} avis)
                    </span>
                </div>

                {/* Specializations */}
                {agent.specializations && agent.specializations.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                        {agent.specializations.slice(0, 3).map((spec, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs">
                                {spec}
                            </Badge>
                        ))}
                    </div>
                )}

                {/* Commission */}
                {agent.commission_rate && (
                    <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Honoraires</span>
                        <span className="font-semibold text-scalimmo-navy">
                            {agent.commission_rate}%
                        </span>
                    </div>
                )}

                {/* CTA */}
                <Button
                    className={`w-full rounded-full ${isElite ? 'btn-gold' : 'btn-primary'}`}
                    onClick={() => navigate(`/agents/${agent.id}`)}
                    data-testid={`view-agent-${agent.id}`}
                >
                    <Calendar className="w-4 h-4 mr-2" />
                    Prendre rendez-vous
                </Button>
            </div>
        </div>
    );
};
